import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { reportClientError } from '@/lib/observability'
import { refreshToLatestBuild, StaleBuildError } from '@/lib/staleBuild'

// ─── The last thing between a render crash and a white screen ───────────────────
// React unmounts the whole tree when a render throws and nothing catches it. The
// result is a blank page: no message, no console anyone will read, and — because
// the request itself succeeded — nothing in the edge function logs either. The
// only signal was somebody eventually saying "it's just white".
//
// This does two jobs, and both matter:
//   1. reports the crash, with the component stack, so it is diagnosable at all;
//   2. shows the user something other than nothing.
//
// It is deliberately NOT a router-level boundary only. It wraps the app so a
// crash in a layout or a lazy chunk is caught too — those are precisely the ones
// that blank the entire screen rather than one panel.

interface Props {
  children: ReactNode
  /** Names the boundary in the report, e.g. 'app' or 'chat'. */
  source?: string
  /**
   * Clears a caught error whenever this value changes — pass the current route.
   *
   * Without it a single failed screen wedges everything after it: the error state
   * belongs to the boundary, not to the route that produced it, so every later
   * menu click keeps rendering the same message about a screen the operator has
   * already navigated away from. The only way out was the Reload button.
   *
   * Deliberately a prop rather than a `key`: re-keying the boundary would remount
   * its whole subtree on every navigation, and the nav shell is inside it.
   */
  resetKey?: string | number
}

interface State {
  error: Error | null
  /** Holds the button busy while the service worker turns over before the reload. */
  refreshing?: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.reset()
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale build is not a fault and lazyRoute has already recorded it. Reporting
    // it again here would double every deploy-straddling navigation in the logs.
    if (error instanceof StaleBuildError) return

    reportClientError({
      source: this.props.source ?? 'react',
      message: error.message || 'render crash',
      error,
      context: {
        // The component stack is what makes a minified production stack usable.
        // Capped because a deep tree produces a very long string.
        component_stack: info.componentStack?.slice(0, 4000),
      },
    })
  }

  private reset = () => {
    // `refreshing` goes too: navigating away from a stale-build screen mid-reload
    // would otherwise leave the next one showing a disabled "Reloading…" button.
    this.setState({ error: null, refreshing: false })
  }

  private reload = () => {
    window.location.reload()
  }

  private refresh = () => {
    this.setState({ refreshing: true })
    // Never rejects; the page is replaced on the way out.
    void refreshToLatestBuild()
  }

  render() {
    if (!this.state.error) return this.props.children

    // ─── A replaced build is not a crash, and must not read like one ───────────
    // "Something went wrong … reported automatically" is both frightening and
    // wrong here: nothing went wrong, and reloading is a certain fix rather than
    // a hopeful one. The reload is a button rather than automatic because this
    // screen can appear over unsaved work — a note in the assistant panel, a
    // part-filled intake form — and navigating back to a working screen to save
    // it first is a legitimate choice.
    if (this.state.error instanceof StaleBuildError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="space-y-2">
            <h1 className="text-lg font-semibold text-foreground">The app has been updated</h1>
            <p className="max-w-md text-sm text-muted-foreground">
              This screen belongs to a newer version. Reload to open it — anything you have
              typed but not saved will be lost, so finish that first if you need to.
            </p>
          </div>

          <Button onClick={this.refresh} disabled={this.state.refreshing}>
            {this.state.refreshing ? 'Reloading…' : 'Reload'}
          </Button>
        </div>
      )
    }

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            This screen ran into an error and could not finish loading. The problem has been
            reported automatically.
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={this.reset}>Try again</Button>
          <Button onClick={this.reload}>Reload the app</Button>
        </div>

        {/* The message is shown only in development. In production it can carry
            internal detail, and it means nothing to an operator anyway. */}
        {import.meta.env.DEV && (
          <pre className="mt-4 max-w-full overflow-x-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
        )}
      </div>
    )
  }
}
